import { type Except, notEmpty, type Optional } from '@festina-life/core'
import { Big } from 'big.js'
import { useCallback, useMemo } from 'react'
import {
  NumericFormat,
  numericFormatter,
  type OnValueChange,
} from 'react-number-format'

import { useNumberFormatOptions } from '../numberInput/useNumberFormatOptions'
import { RawTextInput, type RawTextInputProps } from '../textInput/RawTextInput'

export type RawPercentageInputProps = Except<
  RawTextInputProps,
  'value' | 'onChange' | 'type' | 'defaultValue' | 'placeholder'
> & {
  value?: Optional<number>
  name?: string
  defaultValue?: Optional<string | number>
  placeholder?: Optional<string | number>
  onChange?: (value: number | undefined) => void
  decimalScale?: number
}

export const DEFAULT_PERCENT_DECIMAL_SCALE = 8

const SUFFIX = ' %'

export const RawPercentageInput = ({
  value,
  onChange,
  placeholder,
  decimalScale = DEFAULT_PERCENT_DECIMAL_SCALE,
  ...restProps
}: RawPercentageInputProps) => {
  const numberFormatOptions = useNumberFormatOptions(decimalScale)

  const convertedInternalValue = useMemo(
    () =>
      notEmpty(value)
        ? new Big(value).mul(100).round(decimalScale).toNumber()
        : null,
    [decimalScale, value],
  )

  const formattedPlaceholder = useMemo(() => {
    let parsedPlaceholder = placeholder

    if (typeof placeholder === 'string') {
      parsedPlaceholder = isNaN(parseFloat(placeholder))
        ? placeholder
        : new Big(placeholder).mul(100).round(decimalScale).toNumber()
    } else if (typeof placeholder === 'number') {
      parsedPlaceholder = new Big(placeholder)
        .mul(100)
        .round(decimalScale)
        .toNumber()
    }

    return `${numericFormatter(
      parsedPlaceholder?.toString() ?? '',
      numberFormatOptions,
    )}${typeof parsedPlaceholder === 'number' ? SUFFIX : ''}`
  }, [decimalScale, numberFormatOptions, placeholder])

  const handleChange: OnValueChange = useCallback(
    ({ value }) => {
      // Manually convert visible percentage to internal percentage value

      const convertedNumber = value
        ? new Big(value).div(100).round(decimalScale).toNumber()
        : undefined

      onChange?.(convertedNumber)
    },
    [decimalScale, onChange],
  )

  return (
    <NumericFormat
      value={convertedInternalValue}
      suffix={SUFFIX}
      placeholder={formattedPlaceholder}
      onValueChange={handleChange}
      size={(convertedInternalValue?.toString().length ?? 0) + 1} // Without this inputs have size 20 as default dictating a too wide min width — rawTextInputStyles sets w-full so it fills container.
      {...restProps}
      customInput={RawTextInput}
      {...numberFormatOptions}
    />
  )
}
